import type { WidgetConfig } from '../theme.js';
import { useWidgetData } from '../api.js';
import { EmptyState, ErrorState, Frame, Loading, UnavailableState } from '../chrome.js';
import { formatDayHeading, formatStamp, formatTime, groupByDay } from '../format.js';

/**
 * Economic calendar.
 *
 * Grouped by day with a sticky-looking day header row, which is how every
 * trader already reads a calendar. Events carry only a headline, a time and a
 * link back to the issuing institution — never any of the release body, which
 * is both the licence position for these sources and the right editorial one.
 */

interface CalendarEvent {
  id: string;
  title: string;
  country: string | null;
  currency: string | null;
  category: string | null;
  importance: 'low' | 'medium' | 'high';
  scheduled_at: string;
  time_known: boolean;
  actual: string | null;
  previous: string | null;
  /** Period each figure describes ('2026-07'), rendered as a small qualifier. */
  actual_period: string | null;
  previous_period: string | null;
  link: string | null;
}

const TITLE = 'Economic calendar';
const IMPACT_DOTS = { low: 1, medium: 2, high: 3 } as const;

export function Calendar({ config }: { config: WidgetConfig }) {
  const state = useWidgetData<CalendarEvent[]>(config, '/v1/calendar', { days: '7' });

  if (state.status === 'loading') return <Loading title={TITLE} rows={6} />;
  if (state.status === 'error') return <ErrorState title={TITLE} message={state.message} />;
  if (state.status === 'unavailable')
    return (
      <UnavailableState
        title={TITLE}
        message={state.message}
        lastGoodAt={state.lastGoodAt}
        locale={config.locale}
        timeZone={config.timeZone}
      />
    );

  const { envelope, stale } = state;
  const days = groupByDay(envelope.data, (e) => e.scheduled_at, config.timeZone);

  // Whether any event in view has published numbers. When none do — which is
  // the normal case for a forward-looking week — the result columns are dropped
  // entirely rather than rendered empty, saving width in a widget that is often
  // placed in a sidebar.
  //
  // There are two columns here, not three. Consensus forecast is a survey of
  // private economists rather than a statistic: no agency publishes one and
  // every commercial source forbids redistribution, so the column was removed
  // outright rather than shipped as a permanent em-dash.
  const hasResults = envelope.data.some((e) => e.actual !== null || e.previous !== null);

  return (
    <Frame
      title={TITLE}
      stamp={envelope.as_of ? formatStamp(envelope.as_of, config.locale, config.timeZone) : undefined}
      stale={stale}
      attributions={envelope.attributions}
      locale={config.locale}
    >
      {envelope.data.length === 0 ? (
        <EmptyState message="No releases are scheduled for the week ahead." />
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">Event</th>
              {hasResults && (
                <>
                  <th scope="col" className="num">
                    Actual
                  </th>
                  <th scope="col" className="num">
                    Prev
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {days.map(([day, events]) => (
              <Day
                key={day}
                day={day}
                events={events}
                config={config}
                hasResults={hasResults}
              />
            ))}
          </tbody>
        </table>
      )}
    </Frame>
  );
}

function Day({
  day,
  events,
  config,
  hasResults,
}: {
  day: string;
  events: CalendarEvent[];
  config: WidgetConfig;
  hasResults: boolean;
}) {
  const columns = hasResults ? 4 : 2;

  return (
    <>
      <tr className="day-head">
        <td colSpan={columns}>{formatDayHeading(day, config.locale)}</td>
      </tr>

      {events.map((event) => (
        <tr key={event.id}>
          <td className="muted" style={{ whiteSpace: 'nowrap' }}>
            {/* Several sources publish a date with no time. Rendering that as
                00:00 would assert a midnight release that is not real. */}
            {event.time_known ? formatTime(event.scheduled_at, config.locale, config.timeZone) : 'All day'}
          </td>

          <td>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {event.country && <span className="country">{event.country}</span>}
              <Impact level={event.importance} />
              {event.link ? (
                <a
                  className="event-title"
                  href={event.link}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {event.title}
                </a>
              ) : (
                <span className="event-title">{event.title}</span>
              )}
            </span>
          </td>

          {hasResults && (
            <>
              <td className="num">
                <Figure value={event.actual} period={event.actual_period} />
              </td>
              <td className="num">
                <Figure value={event.previous} period={event.previous_period} />
              </td>
            </>
          )}
        </tr>
      ))}
    </>
  );
}

/**
 * One published figure, with the period it describes underneath it.
 *
 * The period is not decoration. Agencies publish on their own cadence and some
 * series lag by months; "1.9%" alone is indistinguishable from a fresh reading,
 * whereas "1.9% / Dec 2025" tells the reader exactly what they are looking at.
 */
function Figure({ value, period }: { value: string | null; period: string | null }) {
  if (value === null) return <span className="muted">—</span>;
  return (
    <>
      <span>{value}</span>
      {period && <span className="period">{formatPeriod(period)}</span>}
    </>
  );
}

/**
 * '2026-07' -> 'Jul 2026', '2026-08-15' -> '15 Aug 2026'.
 *
 * Deliberately not Intl-formatted against the widget locale: these are period
 * labels, not instants, and constructing a Date from 'YYYY-MM' to feed a
 * formatter would reintroduce exactly the midnight-timezone bug that DATE
 * parsing was fixed for on the server.
 */
function formatPeriod(period: string): string {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const parts = period.split('-');
  const year = parts[0];
  const month = parts[1] ? MONTHS[Number(parts[1]) - 1] : undefined;
  if (!month) return period;
  const day = parts[2];
  return day ? `${Number(day)} ${month} ${year}` : `${month} ${year}`;
}

function Impact({ level }: { level: 'low' | 'medium' | 'high' }) {
  const filled = IMPACT_DOTS[level];
  return (
    <span className="impact" role="img" aria-label={`${level} importance`}>
      {[1, 2, 3].map((n) => (
        <i key={n} className={n <= filled ? 'on' : ''} />
      ))}
    </span>
  );
}
